import React, { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Icon,
  CircularProgress,
  Theme,
  useTheme,
  SxProps,
} from '@mui/material';
import { FirebaseError } from 'firebase/app';
import Spacer from '@components/Utils/Spacer';
import { authService } from '@features/auth/services/authService';

type PageState = 'loading' | 'form' | 'logging-in' | 'success' | 'expired' | 'resent';

const SetPasswordPage: React.FC = () => {
  const theme = useTheme();
  const sxStyles = useSxStyles(theme);
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const oobCode = searchParams.get('oobCode') || '';
  const emailFromParams = searchParams.get('email') || '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [email, setEmail] = useState<string>(emailFromParams);
  const [resendEmail, setResendEmail] = useState<string>(emailFromParams);
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Sign out any existing session then validate the OOB code
  useEffect(() => {
    if (!oobCode) {
      setPageState('expired');
      return;
    }

    const init = async () => {
      // Sign out any existing session — reset links are standalone auth flows
      await authService.logout().catch(() => {});

      try {
        const userEmail = await authService.verifyPasswordResetCode(oobCode);
        setEmail(userEmail);
        setPageState('form');
      } catch {
        setPageState('expired');
      }
    };

    init();
  }, [oobCode]);

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    setError(null);

    if (!password) {
      setError('Please enter a password.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await authService.confirmPasswordReset(oobCode, password);
      setPageState('logging-in');
      try {
        await authService.login(email, password);
        navigate('/');
        return;
      } catch {
        // Auto-login failed, fall back to success page
        setPageState('success');
      }
    } catch (err) {
      if (err instanceof FirebaseError) {
        if (err.code === 'auth/expired-action-code') {
          setPageState('expired');
        } else if (err.code === 'auth/weak-password') {
          setError('Password is too weak. Please use at least 6 characters.');
        } else {
          setError('Failed to set password. Please try again or contact support.');
        }
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Container maxWidth='sm' sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
        <Paper variant='custom' sx={{ position: 'relative', overflow: 'hidden', padding: '36px 24px' }}>
          {pageState === 'loading' && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <CircularProgress />
              <Spacer size='md' />
              <Typography>Validating your link...</Typography>
            </div>
          )}

          {pageState === 'expired' && (
            <>
              <Typography sx={sxStyles.titleTxt}>Link Expired</Typography>
              <Spacer size='sm' />
              <Typography sx={sxStyles.subtitleTxt}>
                This password link has expired or has already been used. Enter your email below to receive a new link.
              </Typography>
              <Spacer size='lg' />

              <TextField
                required
                fullWidth
                autoFocus
                id='resend-email'
                name='email'
                autoComplete='email'
                label='Email'
                placeholder='Enter your email...'
                variant='outlined'
                size='small'
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                disabled={submitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment style={{ verticalAlign: 'top' }} position='start'>
                      <Icon color='disabled' fontSize='small'>
                        email
                      </Icon>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <>
                  <Spacer size='xs' />
                  <Typography sx={sxStyles.errorTxt}>{error}</Typography>
                </>
              )}

              <Spacer size='lg' />

              <Button
                fullWidth
                color='primary'
                size='large'
                disabled={!resendEmail || submitting}
                onClick={async () => {
                  setError(null);
                  setSubmitting(true);
                  try {
                    await authService.resetPassword(resendEmail);
                    setPageState('resent');
                  } catch {
                    setError('Failed to send. Please check your email and try again.');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitting ? 'Sending...' : 'Send New Link'}
              </Button>

              <Spacer size='sm' />

              <Button fullWidth size='large' onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </>
          )}

          {pageState === 'resent' && (
            <>
              <div style={{ textAlign: 'center' }}>
                <Icon sx={{ fontSize: 48, color: theme.palette.success.main }}>mark_email_read</Icon>
              </div>
              <Spacer size='sm' />
              <Typography sx={sxStyles.titleTxt}>New Link Sent</Typography>
              <Spacer size='sm' />
              <Typography sx={sxStyles.subtitleTxt}>
                Check your email for a new password link. It may take a moment to arrive.
              </Typography>
              <Spacer size='lg' />
              <Button fullWidth size='large' onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </>
          )}

          {pageState === 'form' && (
            <>
              <Typography sx={sxStyles.titleTxt}>Set Your Password</Typography>
              <Spacer size='xs' />
              <Typography sx={sxStyles.subtitleTxt}>{email}</Typography>
              <Spacer size='lg' />

              <TextField
                required
                fullWidth
                autoFocus
                type='password'
                label='Password'
                placeholder='Enter password...'
                variant='outlined'
                size='small'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment style={{ verticalAlign: 'top' }} position='start'>
                      <Icon color='disabled' fontSize='small'>
                        lock
                      </Icon>
                    </InputAdornment>
                  ),
                }}
              />

              <Spacer />

              <TextField
                required
                fullWidth
                type='password'
                label='Confirm Password'
                placeholder='Confirm password...'
                variant='outlined'
                size='small'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                disabled={submitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment style={{ verticalAlign: 'top' }} position='start'>
                      <Icon color='disabled' fontSize='small'>
                        lock
                      </Icon>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <>
                  <Spacer size='xs' />
                  <Typography sx={sxStyles.errorTxt}>{error}</Typography>
                </>
              )}

              <Spacer size='lg' />

              <Button
                fullWidth
                type='submit'
                color='primary'
                size='large'
                onClick={(e) => handleSubmit(e)}
                disabled={submitting}
              >
                {submitting ? 'Setting password...' : 'Set Password'}
              </Button>
            </>
          )}

          {pageState === 'logging-in' && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <CircularProgress />
              <Spacer size='md' />
              <Typography>Logging you in...</Typography>
            </div>
          )}

          {pageState === 'success' && (
            <>
              <div style={{ textAlign: 'center' }}>
                <Icon sx={{ fontSize: 48, color: theme.palette.success.main }}>check_circle</Icon>
              </div>
              <Spacer size='sm' />
              <Typography sx={sxStyles.titleTxt}>Password Set Successfully</Typography>
              <Spacer size='sm' />
              <Typography sx={sxStyles.subtitleTxt}>
                You can now sign in with your new password.
              </Typography>
              <Spacer size='lg' />
              <Button fullWidth color='primary' size='large' onClick={() => navigate('/login')}>
                Sign In
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
};

const useStyles = () => ({
  container: css`
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom right, #e8f4ff, #ffe8e8);
  `,
});

const useSxStyles = (theme: Theme): Record<string, SxProps<Theme>> => ({
  titleTxt: {
    textAlign: 'center',
    fontSize: 24,
    fontWeight: 500,
  },
  subtitleTxt: {
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  errorTxt: {
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.error.main,
  },
});

export default SetPasswordPage;
